import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Snackbar from '@mui/material/Snackbar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import LeagueListPage from './pages/LeagueListPage';
import CreateLeaguePage from './pages/CreateLeaguePage';
import JoinLeaguePage from './pages/JoinLeaguePage';
import LeagueDashboardPage from './pages/LeagueDashboardPage';
import SquadSelectionPage from './pages/SquadSelectionPage';
import LeaderboardPage from './pages/LeaderboardPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ViewTeamsPage from './pages/ViewTeamsPage';
import ProfilePage from './pages/ProfilePage';
import ProfileSetupPage from './pages/ProfileSetupPage';
import UserProfilePage from './pages/UserProfilePage';
import AdminPage from './pages/AdminPage';
import PlayerPoolManagementPage from './pages/PlayerPoolManagementPage';
import TennisPlayerPoolManagementPage from './pages/TennisPlayerPoolManagementPage';
import EditLeaguePage from './pages/EditLeaguePage';
import LeagueRulesPage from './pages/LeagueRulesPage';
import PredictionsViewPage from './pages/PredictionsViewPage';
import PowerplayManagementPage from './pages/PowerplayManagementPage';
import HiddenPlayerManagementPage from './pages/HiddenPlayerManagementPage';
import GlobalLeaderboardPage from './pages/GlobalLeaderboardPage';
import MatchSchedulePage from './pages/MatchSchedulePage';
import ScheduleUploadPage from './pages/ScheduleUploadPage';
import ScoringSystemPage from './pages/ScoringSystemPage';
import LeagueInvitePage from './pages/LeagueInvitePage';
import TennisHubPage from './pages/TennisHubPage';

// Layouts
import LeagueLayout from './components/layouts/LeagueLayout';
import InstallPromptBanner from './components/common/InstallPromptBanner';
import PullToRefresh from './components/common/PullToRefresh';
import WhatsNewModal from './components/common/WhatsNewModal';
import { Analytics } from '@vercel/analytics/react';
import { isEnabled } from './config/features';

// Create Material-UI theme with Satoshi font
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#1E88E5',
    },
    secondary: {
      main: '#00e5ff',
    },
    background: {
      default: '#060D17',
      paper: '#003E5C',
    },
  },
  typography: {
    fontFamily: "'Satoshi', 'Montserrat', 'Segoe UI', sans-serif",
    h1: {
      fontFamily: "'Satoshi', sans-serif",
      fontWeight: 700,
    },
    h2: {
      fontFamily: "'Satoshi', sans-serif",
      fontWeight: 700,
    },
    h3: {
      fontFamily: "'Satoshi', sans-serif",
      fontWeight: 600,
    },
    h4: {
      fontFamily: "'Satoshi', sans-serif",
      fontWeight: 800,
    },
    h5: {
      fontFamily: "'Satoshi', sans-serif",
      fontWeight: 700,
    },
    h6: {
      fontFamily: "'Satoshi', sans-serif",
      fontWeight: 700,
    },
    button: {
      fontFamily: "'Satoshi', sans-serif",
      fontWeight: 600,
      textTransform: 'none',
    },
    body1: {
      fontFamily: "'Satoshi', sans-serif",
      fontWeight: 400,
    },
    body2: {
      fontFamily: "'Satoshi', sans-serif",
      fontWeight: 400,
    },
  },
});

function App() {
  const [showUpdateSnackbar, setShowUpdateSnackbar] = useState(false);

  useEffect(() => {
    const handleNewVersion = () => {
      setShowUpdateSnackbar(true);
    };
    window.addEventListener('fcl:new-version', handleNewVersion);
    return () => window.removeEventListener('fcl:new-version', handleNewVersion);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Analytics />
      {isEnabled('whatsNewModal') && <WhatsNewModal />}
      {isEnabled('installPrompt') && <InstallPromptBanner />}
      {isEnabled('pullToRefresh') && <PullToRefresh />}
      <Snackbar
        open={showUpdateSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowUpdateSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, bgcolor: 'rgba(13,31,53,0.97)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}>
          <Typography sx={{ color: '#1E88E5', fontSize: '1rem', lineHeight: 1 }}>✓</Typography>
          <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 500 }}>You're all set on the latest version</Typography>
        </Box>
      </Snackbar>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            {isEnabled('landing') && <Route path="/" element={<LandingPage />} />}
            {isEnabled('login') && <Route path="/login" element={<LoginPage />} />}
            {isEnabled('register') && <Route path="/register" element={<RegisterPage />} />}
            {isEnabled('inviteLink') && <Route path="/invite/:code" element={<LeagueInvitePage />} />}

            {/* Profile Setup Route */}
            {isEnabled('profileSetup') && <Route path="/profile-setup" element={
              <ProtectedRoute>
                <ProfileSetupPage />
              </ProtectedRoute>
            } />}

            {/* Protected Routes */}
            {isEnabled('dashboard') && <Route path="/dashboard" element={
              <ProtectedRoute>
                <LeagueListPage />
              </ProtectedRoute>
            } />}

            {/* League Management */}
            {isEnabled('createLeague') && <Route path="/leagues/create" element={
              <ProtectedRoute adminOnly={true}>
                <CreateLeaguePage />
              </ProtectedRoute>
            } />}

            {isEnabled('joinLeague') && <Route path="/leagues/join" element={
              <ProtectedRoute>
                <JoinLeaguePage />
              </ProtectedRoute>
            } />}

            {isEnabled('leagueDashboard') && <Route path="/leagues/:leagueId" element={
              <ProtectedRoute>
                <LeagueLayout>
                  <LeagueDashboardPage />
                </LeagueLayout>
              </ProtectedRoute>
            } />}

            {isEnabled('editLeague') && <Route path="/leagues/:leagueId/edit" element={
              <ProtectedRoute>
                <EditLeaguePage />
              </ProtectedRoute>
            } />}

            {isEnabled('squadSelection') && <Route path="/leagues/:leagueId/squad" element={
              <ProtectedRoute>
                <LeagueLayout>
                  <SquadSelectionPage />
                </LeagueLayout>
              </ProtectedRoute>
            } />}

            {isEnabled('leaderboard') && <Route path="/leagues/:leagueId/leaderboard" element={
              <ProtectedRoute>
                <LeagueLayout>
                  <LeaderboardPage />
                </LeagueLayout>
              </ProtectedRoute>
            } />}

            {isEnabled('analytics') && <Route path="/leagues/:leagueId/analytics" element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            } />}

            {isEnabled('viewTeams') && <Route path="/leagues/:leagueId/teams" element={
              <ProtectedRoute>
                <ViewTeamsPage />
              </ProtectedRoute>
            } />}

            {isEnabled('leagueRules') && <Route path="/leagues/:leagueId/rules" element={
              <ProtectedRoute>
                <LeagueRulesPage />
              </ProtectedRoute>
            } />}

            {isEnabled('matchSchedule') && <Route path="/leagues/:leagueId/schedule" element={
              <ProtectedRoute>
                <MatchSchedulePage />
              </ProtectedRoute>
            } />}

            {isEnabled('scoringSystem') && <Route path="/leagues/:leagueId/scoring" element={
              <ProtectedRoute>
                <ScoringSystemPage />
              </ProtectedRoute>
            } />}

            {isEnabled('scheduleUpload') && <Route path="/leagues/:leagueId/schedule/upload" element={
              <ProtectedRoute>
                <ScheduleUploadPage />
              </ProtectedRoute>
            } />}

            {isEnabled('profile') && <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />}

            {isEnabled('userProfile') && <Route path="/user/:userId" element={
              <ProtectedRoute>
                <UserProfilePage />
              </ProtectedRoute>
            } />}

            {/* Admin Routes */}
            {isEnabled('cricketPlayerPool') && <Route path="/admin/player-pools" element={
              <ProtectedRoute adminOnly={true}>
                <PlayerPoolManagementPage />
              </ProtectedRoute>
            } />}

            {isEnabled('tennisPlayerPool') && <Route path="/admin/tennis-player-pools" element={
              <ProtectedRoute adminOnly={true}>
                <TennisPlayerPoolManagementPage />
              </ProtectedRoute>
            } />}

            {isEnabled('predictions') && <Route path="/admin/predictions" element={
              <ProtectedRoute adminOnly={true}>
                <PredictionsViewPage />
              </ProtectedRoute>
            } />}

            {isEnabled('powerplay') && <Route path="/admin/powerplay" element={
              <ProtectedRoute adminOnly={true}>
                <PowerplayManagementPage />
              </ProtectedRoute>
            } />}

            {isEnabled('hiddenPlayers') && <Route path="/admin/hidden-player" element={
              <ProtectedRoute adminOnly={true}>
                <HiddenPlayerManagementPage />
              </ProtectedRoute>
            } />}

            {isEnabled('globalLeaderboard') && <Route path="/admin/global-leaderboard" element={
              <ProtectedRoute adminOnly={true}>
                <GlobalLeaderboardPage />
              </ProtectedRoute>
            } />}

            {isEnabled('adminPanel') && <Route path="/admin/*" element={
              <ProtectedRoute adminOnly={true}>
                <AdminPage />
              </ProtectedRoute>
            } />}

            {/* Tennis Hub */}
            {isEnabled('tennisHub') && <Route path="/tennis" element={
              <ProtectedRoute>
                <TennisHubPage />
              </ProtectedRoute>
            } />}

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
